import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, CircularProgress, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { getList } from "../../dataProvider/API";

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  return (
    payload.dtos ||
    payload.data ||
    payload.items ||
    payload.content ||
    payload.rows ||
    []
  );
};

const toNull = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str === "" ? null : str;
};

const toNumberId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const getVenueTitle = (option) => {
  const fullVenueName =
    toNull(option?.fullVenueName) ||
    toNull(option?.full_venue_name) ||
    toNull(option?.fullVenueLabel);
  if (fullVenueName) return fullVenueName;

  const address = option?.addressDto || {};
  const city = toNull(option?.city) || toNull(address?.city);
  const state = toNull(option?.state) || toNull(address?.state);

  const parts = [
    toNull(option?.aquaticCenter),
    toNull(option?.poolName),
    toNull(option?.building),
    toNull(option?.schoolInstitution),
    [city, state].filter(Boolean).join(", "),
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(", ");

  const clone = { ...option };
  delete clone.id;
  delete clone.addressDto;
  const fallback = Object.values(clone)
    .filter((e) => !!e && typeof e !== "object")
    .join(", ");

  return fallback || `Venue #${option?.id}`;
};

const normalizeOption = (option) => {
  const id = toNumberId(option?.id);
  if (id == null) return null;

  return {
    ...option,
    id,
    title: getVenueTitle(option),
  };
};

const normalizeOptions = (list) => {
  const byId = new Map();

  (Array.isArray(list) ? list : []).forEach((item) => {
    const option = normalizeOption(item);
    if (!option) return;
    byId.set(option.id, option);
  });

  return Array.from(byId.values());
};

const SelectCheckboxFilter = ({
  field,
  label,
  size,
  options,
  searchConfig,
  helperText,
}) => {
  const { value, onChange } = field;
  const normalizedSelected = useMemo(() => normalizeOptions(value), [value]);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const [searchInput, setSearchInput] = useState("");
  const [searchedOptions, setSearchedOptions] = useState(normalizedOptions);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  const isSearchEnabled = Boolean(searchConfig?.getEndpoint);
  const minSearchChars = searchConfig?.minSearchChars ?? 0;
  const debounceMs = searchConfig?.debounceMs ?? 500;

  const mergedOptions = useMemo(() => {
    return normalizeOptions([
      ...normalizedSelected,
      ...(isSearchEnabled ? searchedOptions : normalizedOptions),
    ]);
  }, [isSearchEnabled, normalizedOptions, normalizedSelected, searchedOptions]);

  useEffect(() => {
    if (isSearchEnabled) return;
    setSearchedOptions(normalizedOptions);
  }, [isSearchEnabled, normalizedOptions]);

  const runSearch = async (query) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    try {
      const endpoint = searchConfig.getEndpoint(query);
      const payload = await getList(endpoint, searchConfig?.params);
      if (requestId !== requestIdRef.current) return;

      const nextOptions = normalizeOptions(toArray(payload));
      setSearchedOptions(normalizeOptions([...normalizedSelected, ...nextOptions]));
    } catch (_) {
      if (requestId !== requestIdRef.current) return;
      setSearchedOptions(normalizedSelected);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (_, nextInput, reason) => {
    setSearchInput(nextInput || "");

    if (!isSearchEnabled || (reason !== "input" && reason !== "clear")) {
      return;
    }

    clearTimeout(searchTimeoutRef.current);
    requestIdRef.current += 1;

    const query = String(nextInput || "").trim();
    if (query.length < minSearchChars) {
      setLoading(false);
      setSearchedOptions(normalizedSelected);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      runSearch(query);
    }, debounceMs);
  };

  const handleChange = (_, newValue) => onChange(normalizeOptions(newValue));

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  return (
    <Autocomplete
      multiple
      id="checkboxes-tags-demo"
      options={mergedOptions}
      value={normalizedSelected}
      inputValue={searchInput}
      onInputChange={handleInputChange}
      onChange={handleChange}
      disableCloseOnSelect
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.title || ""}
      getOptionSelected={(option, value) =>
        option?.id === value?.id
      }
      renderOption={(option, { selected }) => (
        <React.Fragment>
          <Checkbox
            style={{ marginRight: 8 }}
            checked={selected}
            color="primary"
          />
          {option?.title || ""}
        </React.Fragment>
      )}
      size={size}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={isSearchEnabled ? "Type to search venues" : "Search"}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

SelectCheckboxFilter.propTypes = {
  options: PropTypes.array.isRequired,
  field: PropTypes.object,
  label: PropTypes.string,
  size: PropTypes.string,
  searchConfig: PropTypes.shape({
    getEndpoint: PropTypes.func,
    params: PropTypes.object,
    minSearchChars: PropTypes.number,
    debounceMs: PropTypes.number,
  }),
  helperText: PropTypes.string,
};

SelectCheckboxFilter.defaultProps = {
  searchConfig: null,
  helperText: "",
};

export default SelectCheckboxFilter;
